/**
 * Inbox — Archive Dispatch
 *
 * Reddit/Discord-style 3-pane secure messaging:
 *   [Tabs: Inbox · Sent · Archived · Settings] [Thread list] [Message view + reply]
 *
 * Privacy contract:
 *   • Recipient picker is callsign-only (live autocomplete).
 *   • Email addresses NEVER appear in this UI.
 *   • Members can Block · Mute · Report · Archive their own threads.
 *
 * Mobile: single-column view that toggles between thread-list ↔ thread-detail.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, Send, Archive, ArchiveRestore, BellOff, Bell, Search,
  Shield, Flag, Star, X, ArrowLeft, Plus, Loader2, RefreshCw,
  Inbox as InboxIcon, FileText, Settings as SettingsIcon, EyeOff, Eye,
  AlertTriangle, ChevronRight,
} from "lucide-react";
import {
  dispatchInbox, dispatchSent, dispatchArchived, dispatchThread,
  dispatchSend, dispatchMarkRead, dispatchArchive, dispatchUnarchive,
  dispatchMute, dispatchUnmute, dispatchBlock, dispatchUnblock,
  dispatchBlocksList, dispatchReport, dispatchUnreadCount,
  dispatchGetPrefs, dispatchSetPrefs, dispatchMemberSearch,
} from "../lib/api";
import { useMemberAuth, setMemberSession } from "../hooks/useMemberAuth";
import { FoundingBadge, foundingCallsignColor } from "../components/founding";

const TABS = [
  { key: "inbox",    label: "Inbox",    icon: InboxIcon },
  { key: "sent",     label: "Sent",     icon: Send },
  { key: "archived", label: "Archived", icon: Archive },
  { key: "blocks",   label: "Blocks",   icon: Shield },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

const TIME = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const now = Date.now();
  const s = Math.floor((now - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
};

export default function Inbox() {
  const { member, token } = useMemberAuth();
  const nav = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("inbox");
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(params.threadId || null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerPrefill, setComposerPrefill] = useState(null);

  // A revoked/expired session must surface ONCE as a session problem —
  // never as an endless stream of "Failed to load" toasts.
  const handleAuthError = useCallback(() => {
    toast.error("Session expired — please sign in again.", { id: "member-session-expired" });
    setMemberSession(null);
  }, []);

  // Open composer pre-filled when arriving from /member/:callsign?to=…
  useEffect(() => {
    const to = searchParams.get("to");
    if (to && !composerOpen) {
      setComposerPrefill({ callsign: to.toUpperCase() });
      setComposerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (!member) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="inbox-unauth">
        <div className="text-center max-w-md">
          <Mail className="mx-auto mb-3 text-[#D92D20]" size={28} />
          <h1 className="font-display text-3xl text-[#E4DFD3] mb-2">Archive Dispatch</h1>
          <p className="text-[#A39E93] mb-4">Sign in to read your secure dispatches.</p>
          <button onClick={() => nav("/auth/login?next=/inbox")} className="bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0907] text-[#E4DFD3]" data-testid="inbox-shell">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-1">// ARCHIVE DISPATCH</div>
            <h1 className="font-display text-4xl">Secure Channel</h1>
            <p className="text-sm text-[#A39E93] mt-1">
              Private operative-to-operative communications. Callsigns only. Never email.
            </p>
          </div>
          <button onClick={() => setComposerOpen(true)} data-testid="inbox-new-dispatch"
            className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Plus size={12} /> New Dispatch
          </button>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-[#2D2A26] mb-4" data-testid="inbox-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setActiveId(null); }}
                data-testid={`inbox-tab-${t.key}`}
                className={"flex items-center gap-2 px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                  (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
                <t.icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-12 gap-3">
          <div className={"lg:col-span-4 " + (activeId ? "hidden lg:block" : "")}>
            {(tab === "inbox" || tab === "sent" || tab === "archived") && (
              <ThreadList tab={tab} token={token} activeId={activeId} onPick={setActiveId}
                onCompose={() => setComposerOpen(true)} onAuthError={handleAuthError} />
            )}
            {tab === "blocks" && <BlocksPanel token={token} onAuthError={handleAuthError} />}
            {tab === "settings" && <SettingsPanel token={token} onAuthError={handleAuthError} />}
          </div>
          <div className={"lg:col-span-8 " + (!activeId ? "hidden lg:block" : "")}>
            {activeId ? (
              <ThreadView threadId={activeId} token={token} onBack={() => setActiveId(null)} onChanged={() => setActiveId(activeId)} />
            ) : (
              <div className="border border-dashed border-[#2D2A26] bg-[#0F0F0F] p-10 text-center font-mono text-xs text-[#757065]" data-testid="inbox-empty-state">
                Select a dispatch to read.
              </div>
            )}
          </div>
        </div>
      </div>

      {composerOpen && (
        <Composer token={token} prefill={composerPrefill} onClose={() => { setComposerOpen(false); setComposerPrefill(null); }}
          onSent={(tid) => { setComposerOpen(false); setComposerPrefill(null); setTab("inbox"); setActiveId(tid); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Thread list
// ---------------------------------------------------------------------

function ThreadList({ tab, token, activeId, onPick, onCompose, onAuthError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // null = not yet loaded · true = last load succeeded · false = last load failed
  const [loadedOk, setLoadedOk] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = tab === "inbox" ? dispatchInbox : tab === "sent" ? dispatchSent : dispatchArchived;
      const r = await fn(token, { limit: 50 });
      setRows(r.rows || []);
      setLoadedOk(true);
    } catch (e) {
      // A failure is NOT an empty inbox. Show a stable inline error state;
      // no toast here — the polling loop would repeat it forever.
      setLoadedOk(false);
      if (e?.response?.status === 401) onAuthError?.();
    }
    finally { setLoading(false); }
  }, [tab, token, onAuthError]);

  useEffect(() => { load(); }, [load]);
  // Periodic refresh every 30s while focused
  useEffect(() => {
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const callsigns = (r.participants || []).map((p) => (p.callsign || "").toLowerCase()).join(" ");
      const subj = (r.subject || "").toLowerCase();
      const prev = (r.last_message_preview || "").toLowerCase();
      return callsigns.includes(q) || subj.includes(q) || prev.includes(q);
    });
  }, [rows, search]);

  return (
    <div className="border border-[#2D2A26] bg-[#0a0a0a]" data-testid={`dispatch-list-${tab}`}>
      <div className="border-b border-[#2D2A26] p-2 flex items-center gap-2">
        <Search size={12} className="text-[#757065]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by callsign…" data-testid="dispatch-list-search"
          className="flex-1 bg-transparent outline-none text-xs text-[#E4DFD3] placeholder:text-[#757065]" />
        <button onClick={load} className="text-[#757065] hover:text-[#D92D20]" data-testid="dispatch-list-refresh">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      <div className="divide-y divide-[#2D2A26]/70 max-h-[70vh] overflow-y-auto">
        {loadedOk === false && !loading && (
          <div className="px-4 py-10 text-center" data-testid="dispatch-list-error">
            <AlertTriangle size={16} className="mx-auto mb-2 text-[#D92D20]" />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
              Secure channel unavailable
            </div>
            <div className="font-mono text-xs text-[#757065] mt-2">
              Dispatches could not be retrieved. Your messages are safe.
            </div>
            <button onClick={load} data-testid="dispatch-list-retry"
              className="mt-4 inline-flex items-center gap-2 border border-[#454038] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3] hover:border-[#D92D20] hover:text-[#D92D20]">
              <RefreshCw size={10} /> Retry
            </button>
          </div>
        )}
        {loadedOk === true && filtered.length === 0 && !loading && (
          tab === "inbox" ? (
            <div className="px-4 py-12 text-center" data-testid="dispatch-list-empty">
              <InboxIcon size={18} className="mx-auto mb-3 text-[#454038]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A39E93]">
                No incoming dispatches
              </div>
              <div className="font-mono text-xs text-[#757065] mt-2 leading-relaxed">
                Your secure channel is clear.<br />
                Messages from other investigators will appear here.
              </div>
              <button onClick={onCompose} data-testid="dispatch-empty-new"
                className="mt-5 inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                <Plus size={11} /> New Dispatch
              </button>
            </div>
          ) : (
            <div className="px-4 py-10 text-center font-mono text-xs text-[#757065]" data-testid="dispatch-list-empty">
              {tab === "sent" ? "No sent dispatches." : "No archived dispatches."}
            </div>
          )
        )}
        {filtered.map((t) => {
          const peer = (t.participants && t.participants[0]) || { callsign: "Operative" };
          const isActive = t.id === activeId;
          const unread = t.unread_count > 0;
          return (
            <button key={t.id} onClick={() => onPick(t.id)} data-testid={`dispatch-row-${t.id}`}
              className={"w-full text-left px-3 py-2.5 hover:bg-[#131313] " +
                (isActive ? "bg-[#1a0808]" : "") + (unread ? " border-l-2 border-[#D92D20]" : "")}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-display text-base truncate" style={{ color: foundingCallsignColor(peer.is_founding) }}>
                  {peer.callsign || "?"}
                </span>
                {peer.is_founding && <FoundingBadge size="sm" testid={`dispatch-founding-${t.id}`} />}
                {t.muted && <BellOff size={10} className="text-[#757065]" />}
                <span className="ml-auto font-mono text-[10px] text-[#757065]">{TIME(t.last_message_at)}</span>
              </div>
              {t.channel === "contact-admin" && (
                <span className="inline-block border border-[#CBB892]/50 text-[#CBB892] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.25em] mb-0.5" data-testid={`editorial-desk-tag-${t.id}`}>Editorial Desk</span>
              )}
              {t.subject && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39E93] mb-0.5 truncate">{t.subject}</div>}
              <div className="flex items-center gap-2">
                {t.last_sender_is_me && <span className="font-mono text-[9px] text-[#757065]">YOU:</span>}
                <span className={"text-xs truncate " + (unread ? "text-[#E4DFD3] font-semibold" : "text-[#A39E93]")}>
                  {t.last_message_preview || "—"}
                </span>
                {unread && (
                  <span className="ml-auto inline-flex items-center justify-center bg-[#D92D20] text-white text-[10px] font-mono px-1.5 rounded-sm" data-testid={`dispatch-unread-${t.id}`}>
                    {t.unread_count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Thread view + reply
// ---------------------------------------------------------------------

function ThreadView({ threadId, token, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionMenu, setActionMenu] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await dispatchThread(token, threadId, { limit: 200 });
      setData(r);
      await dispatchMarkRead(token, threadId);
      setTimeout(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "instant" }); }, 50);
    } catch (e) {
      if (e?.response?.status === 401) return void toast.error("Session expired — please sign in again.", { id: "member-session-expired" });
      toast.error("Failed to load thread", { id: "dispatch-thread-load" });
    }
  }, [token, threadId]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="border border-[#2D2A26] bg-[#0a0a0a] p-10 text-center font-mono text-xs text-[#757065]"><Loader2 size={14} className="animate-spin inline" /> Loading…</div>;
  }
  const t = data.thread;
  const peer = (t.participants && t.participants[0]) || {};

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await dispatchSend(token, { thread_id: threadId, body: reply });
      setReply("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    try {
      await dispatchArchive(token, threadId); toast.success("Archived");
      onBack();
    } catch { toast.error("Failed"); }
  };
  const unarchive = async () => {
    try { await dispatchUnarchive(token, threadId); toast.success("Restored"); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const mute = async () => {
    try { await (t.muted ? dispatchUnmute : dispatchMute)(token, threadId); toast.success(t.muted ? "Unmuted" : "Muted"); onChanged(); load(); }
    catch { toast.error("Failed"); }
  };
  const block = async () => {
    if (!peer.id) return;
    if (!window.confirm(`Block ${peer.callsign}? They will not be able to send you new dispatches.`)) return;
    try { await dispatchBlock(token, peer.id); toast.success("Operative blocked"); onBack(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="border border-[#2D2A26] bg-[#0a0a0a] flex flex-col h-[80vh]" data-testid={`dispatch-thread-${threadId}`}>
      {/* Header */}
      <div className="border-b border-[#2D2A26] px-3 py-2 flex items-center gap-2">
        <button onClick={onBack} className="lg:hidden text-[#A39E93] hover:text-[#D92D20]" data-testid="dispatch-thread-back"><ArrowLeft size={16} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg" style={{ color: foundingCallsignColor(peer.is_founding) }}>{peer.callsign || "Operative"}</span>
            {peer.is_founding && <FoundingBadge size="sm" testid={`thread-founding-${threadId}`} />}
            {peer.clearance?.is_director && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#E0A526]">DIR</span>}
            {t.muted && <BellOff size={11} className="text-[#757065]" />}
          </div>
          {t.channel === "contact-admin" && (
            <span className="inline-block border border-[#CBB892]/50 text-[#CBB892] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.25em]" data-testid="editorial-desk-tag-row">Editorial Desk</span>
          )}
          {t.subject && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39E93] truncate">{t.subject}</div>}
        </div>
        <div className="relative">
          <button onClick={() => setActionMenu((v) => !v)} className="text-[#A39E93] hover:text-[#D92D20] px-2 py-1 border border-[#454038]" data-testid="dispatch-thread-actions">⋯</button>
          {actionMenu && (
            <div className="absolute right-0 top-full mt-1 z-20 border border-[#2D2A26] bg-[#0F0F0F] min-w-[180px]" data-testid="dispatch-thread-menu">
              <button onClick={() => { mute(); setActionMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-[#131313] font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                {t.muted ? <Bell size={11} /> : <BellOff size={11} />} {t.muted ? "Unmute" : "Mute"}
              </button>
              {t.archived ? (
                <button onClick={() => { unarchive(); setActionMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-[#131313] font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                  <ArchiveRestore size={11} /> Unarchive
                </button>
              ) : (
                <button onClick={() => { archive(); setActionMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-[#131313] font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                  <Archive size={11} /> Archive
                </button>
              )}
              <button onClick={() => { block(); setActionMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-[#1a0808] text-[#D92D20] font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                <Shield size={11} /> Block Operative
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dispatch-messages">
        {(data.messages || []).map((m) => (
          <MessageBubble key={m.id} m={m} token={token} threadId={threadId} onChanged={load} />
        ))}
      </div>

      {/* Reply */}
      <div className="border-t border-[#2D2A26] p-3">
        <div className="flex gap-2">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
            rows={2} placeholder="Type a dispatch…   Cmd+Enter to send" data-testid="dispatch-reply-input"
            className="flex-1 bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065] resize-none" />
          <button onClick={send} disabled={busy || !reply.trim()} data-testid="dispatch-reply-send"
            className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] self-stretch">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m, token, threadId, onChanged }) {
  const [showReport, setShowReport] = useState(false);
  const hidden = m.status === "hidden" || m.status === "removed" || m.status === "hidden_for_you";
  const isMine = m.is_mine;

  return (
    <div className={"flex " + (isMine ? "justify-end" : "justify-start")} data-testid={`dispatch-msg-${m.id}`}>
      <div className={"max-w-[80%] " + (isMine ? "items-end" : "items-start") + " flex flex-col"}>
        <div className={"px-3 py-2 font-mono text-sm " +
          (isMine ? "bg-[#D92D20]/15 border border-[#D92D20]/40 text-[#E4DFD3]" :
                    "bg-[#131313] border border-[#2D2A26] text-[#E4DFD3]") +
          (hidden ? " italic text-[#757065]" : "")}>
          {hidden ? (m.status === "hidden_for_you" ? "[hidden — you reported this message]" : "[hidden by moderator]") : m.body}
          {m.system && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#E0A526]">[BROADCAST]</span>}
        </div>
        <div className="flex items-center gap-2 mt-1 font-mono text-[9px] text-[#757065]">
          <span>{TIME(m.created_at)}</span>
          {!hidden && !isMine && (
            <button onClick={() => setShowReport(true)} className="hover:text-[#D92D20]" data-testid={`dispatch-report-${m.id}`}><Flag size={10} /></button>
          )}
        </div>
      </div>
      {showReport && (
        <ReportModal messageId={m.id} token={token} onClose={() => setShowReport(false)} onDone={() => { setShowReport(false); onChanged(); }} />
      )}
    </div>
  );
}

function ReportModal({ messageId, token, onClose, onDone }) {
  const [category, setCategory] = useState("harassment");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await dispatchReport(token, { message_id: messageId, category, reason });
      toast.success("Reported. The Owner has been notified.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="report-modal">
      <div className="w-full max-w-md border border-[#D92D20]/40 bg-[#0F0F0F] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// REPORT MESSAGE</div>
          <button onClick={onClose} className="text-[#A39E93] hover:text-[#D92D20]"><X size={14} /></button>
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="report-category"
          className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] mb-2">
          <option value="harassment">Harassment</option>
          <option value="spam">Spam</option>
          <option value="threat">Threat / Violence</option>
          <option value="doxxing">Doxxing</option>
          <option value="off_topic">Off topic</option>
          <option value="other">Other</option>
        </select>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="Optional context for the Owner" data-testid="report-reason"
          className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] mb-3 resize-y" />
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy} data-testid="report-submit" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin inline" /> : "Submit Report"}
          </button>
          <button onClick={onClose} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Composer (new dispatch)
// ---------------------------------------------------------------------

function Composer({ token, onClose, onSent, prefill = null }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [rows, setRows] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const debounce = useRef(null);

  // Pre-fill from deep link (e.g. /member/:callsign → Message Operative)
  useEffect(() => {
    if (prefill?.callsign) {
      setPicked({ callsign: prefill.callsign, id: null, is_founding: false });
    }
  }, [prefill]);

  useEffect(() => {
    if (!query || query.length < 2) { setRows([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const r = await dispatchMemberSearch(token, query);
        setRows(r.rows || []);
      } catch { setRows([]); }
    }, 220);
    return () => clearTimeout(debounce.current);
  }, [query, token]);

  const send = async () => {
    if (!picked) return toast.error("Pick an operative.");
    if (!body.trim()) return toast.error("Message body is required.");
    setBusy(true);
    try {
      const r = await dispatchSend(token, {
        recipient_callsign: picked.callsign, subject, body,
      });
      toast.success("Dispatch sent");
      onSent(r.thread_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" data-testid="composer-modal">
      <div className="w-full max-w-xl sm:border border-[#D92D20]/40 bg-[#0F0F0F] min-h-screen sm:min-h-0 sm:my-8 flex flex-col">
        <div className="flex items-center justify-between border-b border-[#2D2A26] px-4 py-3 shrink-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// NEW DISPATCH</div>
          <button onClick={onClose} className="text-[#A39E93] hover:text-[#D92D20]"><X size={14} /></button>
        </div>
        {/* Scrollable body — keeps the fixed send bar reachable when the
             mobile virtual keyboard opens by giving the form its own
             overflow container. */}
        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">To · Operative callsign, name, interest or investigation</label>
            {picked ? (
              <div className="flex items-center justify-between border border-[#5DD4B3]/40 bg-[#5DD4B3]/10 px-3 py-2" data-testid="composer-selected-chip">
                <span className="font-display text-base" style={{ color: foundingCallsignColor(picked.is_founding) }}>{picked.callsign}</span>
                <button onClick={() => { setPicked(null); setQuery(""); }} className="text-[#A39E93] hover:text-[#D92D20]"><X size={12} /></button>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 border border-[#2D2A26] bg-[#0a0a0a] px-3 py-2">
                  <Search size={12} className="text-[#757065]" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Type 2+ letters — callsign, name, skill or topic…"
                    data-testid="composer-search"
                    className="flex-1 bg-transparent outline-none text-sm text-[#E4DFD3] placeholder:text-[#757065]" />
                </div>
                {/* Results live INSIDE the scroll container (non-absolute)
                     so mobile keyboards don't obscure them and taps land
                     on real DOM. Desktop still gets a compact stacked
                     list beneath the field. */}
                {rows.length > 0 && (
                  <div className="mt-1 border border-[#2D2A26] bg-[#0a0a0a] max-h-64 overflow-y-auto" data-testid="composer-results">
                    {rows.map((m) => (
                      <button key={m.id} onClick={() => { setPicked(m); setRows([]); setQuery(""); }} data-testid={`composer-pick-${m.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#1a0808] border-b border-[#2D2A26]/40 last:border-b-0 active:bg-[#1a0808]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-base" style={{ color: foundingCallsignColor(m.is_founding) }}>{m.callsign}</span>
                          {m.is_founding && <FoundingBadge size="sm" />}
                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39E93]">{m.clearance?.label}</span>
                          {m.display_name && (
                            <span className="font-mono text-[10px] text-[#757065]">· {m.display_name}</span>
                          )}
                        </div>
                        {(m.skills?.length || m.interests?.length) ? (
                          <div className="mt-1 font-mono text-[9px] text-[#757065] truncate">
                            {[...(m.skills || []).slice(0,3), ...(m.interests || []).slice(0,3)].join(" · ")}
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
                {query.length >= 2 && rows.length === 0 && (
                  <div className="text-[10px] font-mono text-[#757065] mt-2">No discoverable operatives match. Hidden operatives never appear here.</div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">Subject (optional)</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief — first dispatch only" data-testid="composer-subject"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]" />
          </div>
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">Message</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Dispatch body…" data-testid="composer-body"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] resize-y" />
          </div>
        </div>
        {/* Sticky action bar — always reachable on mobile even when the
             virtual keyboard pushes the modal up. */}
        <div className="border-t border-[#2D2A26] p-3 flex gap-2 shrink-0 bg-[#0F0F0F]">
          <button onClick={send} disabled={busy || !picked} data-testid="composer-send"
            className="flex-1 flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send Dispatch
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Blocks panel
// ---------------------------------------------------------------------

function BlocksPanel({ token, onAuthError }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await dispatchBlocksList(token); setRows(r.rows || []);
    } catch (e) {
      if (e?.response?.status === 401) return void onAuthError?.();
      toast.error("Failed to load blocks", { id: "dispatch-blocks-load" });
    }
  }, [token, onAuthError]);
  useEffect(() => { load(); }, [load]);

  const unblock = async (m) => {
    if (!window.confirm(`Unblock ${m.callsign}?`)) return;
    try { await dispatchUnblock(token, m.id); toast.success("Unblocked"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="border border-[#2D2A26] bg-[#0a0a0a]" data-testid="dispatch-blocks">
      <div className="border-b border-[#2D2A26] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
        Blocked operatives ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-10 text-center font-mono text-xs text-[#757065]">No blocks.</div>
      ) : (
        <div className="divide-y divide-[#2D2A26]/70">
          {rows.map((m) => (
            <div key={m.id} className="px-3 py-2 flex items-center gap-2" data-testid={`block-row-${m.id}`}>
              <span className="font-display text-base flex-1" style={{ color: foundingCallsignColor(m.is_founding) }}>{m.callsign}</span>
              <span className="font-mono text-[10px] text-[#757065]">{m.blocked_at ? new Date(m.blocked_at).toLocaleDateString() : ""}</span>
              <button onClick={() => unblock(m)} className="border border-[#454038] text-[#A39E93] hover:text-[#5DD4B3] hover:border-[#5DD4B3] px-2 py-1 font-mono text-[10px] uppercase">Unblock</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Settings panel (per-member discoverability + email notif)
// ---------------------------------------------------------------------

function SettingsPanel({ token, onAuthError }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await dispatchGetPrefs(token); setPrefs(r.prefs);
    } catch (e) {
      if (e?.response?.status === 401) return void onAuthError?.();
      toast.error("Failed to load preferences", { id: "dispatch-prefs-load" });
    }
  }, [token, onAuthError]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await dispatchSetPrefs(token, patch); setPrefs(r.prefs);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!prefs) return <div className="border border-[#2D2A26] bg-[#0a0a0a] p-6 font-mono text-xs text-[#757065]">Loading…</div>;

  return (
    <div className="border border-[#2D2A26] bg-[#0a0a0a] p-5 space-y-4" data-testid="dispatch-settings">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] mb-1">DISCOVERABILITY</div>
        <p className="text-xs text-[#A39E93] mb-2">
          When <strong className="text-[#E0A526]">Discoverable</strong>, other operatives can find your callsign and send the first dispatch. When <strong className="text-[#9FA8D4]">Hidden</strong>, only operatives you have already replied to can message you. The Owner can always locate any account.
        </p>
        <div className="flex gap-2">
          <button onClick={() => save({ discoverable: true })} disabled={busy} data-testid="prefs-discoverable-on"
            className={"flex items-center gap-2 px-3 py-2 border font-mono text-[10px] uppercase tracking-[0.25em] " +
              (prefs.discoverable ? "border-[#5DD4B3] text-[#5DD4B3] bg-[#0a1a13]" : "border-[#2D2A26] text-[#A39E93]")}>
            <Eye size={11} /> Discoverable
          </button>
          <button onClick={() => save({ discoverable: false })} disabled={busy} data-testid="prefs-discoverable-off"
            className={"flex items-center gap-2 px-3 py-2 border font-mono text-[10px] uppercase tracking-[0.25em] " +
              (!prefs.discoverable ? "border-[#9FA8D4] text-[#9FA8D4] bg-[#0a0a1a]" : "border-[#2D2A26] text-[#A39E93]")}>
            <EyeOff size={11} /> Hidden (default)
          </button>
        </div>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] mb-1">EMAIL NOTIFICATIONS</div>
        <p className="text-xs text-[#A39E93] mb-2">
          When ON, you receive a brief email <span className="text-[#E4DFD3]">&ldquo;You have a new Archive Dispatch message&rdquo;</span> — never the message body. We never share your email with other operatives.
        </p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!prefs.email_on_new_message} onChange={(e) => save({ email_on_new_message: e.target.checked })} data-testid="prefs-email-toggle" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3]">Email me when I receive a new dispatch</span>
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Unread badge — exported for nav use
// ---------------------------------------------------------------------

export function useDispatchUnreadCount() {
  const { token, member } = useMemberAuth();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!token || !member) { setUnread(0); return; }
    let alive = true;
    const fetch = async () => {
      try {
        const r = await dispatchUnreadCount(token);
        if (alive) setUnread(r.unread || 0);
      } catch { /* noop */ }
    };
    fetch();
    const i = setInterval(fetch, 30000);
    return () => { alive = false; clearInterval(i); };
  }, [token, member]);
  return unread;
}
